import React, { forwardRef, Ref, useEffect, useImperativeHandle, useState } from "react";
import { FormControl, FormControlLabel, Checkbox, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { useFormik } from "formik";
import { useStoreDispatch, useStoreSelector } from "../../../../../../../hooks/StoreHooks";
import { useDoorConfigurationBuilderActions } from "../../../../../../../store/reducers/door_configuration_builder";
import { InstallationType } from "../../../../../../core/common_types";

export type FrmImpostazioniInizialiData = {
  quantita: number,
  vanoConAltroAccesso: boolean,
  portinaPedonaleIntegrata: boolean,
  tipoInstallazioneTraversa: InstallationType,
  tipoInstallazioneMontanteSx: InstallationType,
  tipoInstallazioneMontanteDx: InstallationType,
  isDebordante: boolean,
  larghezzaForo: number,
  altezzaForo: number,
  larghezzaPorta: number,
  altezzaPorta: number,
  profonditaGarage: number,
  altezzaArchitrave: number,
  spessoreArchitrave: number,
  larghezzaSpallettaSx: number,
  spessoreSpallettaSx: number,
  larghezzaSpallettaDx: number,
  spessoreSpallettaDx: number,
}


export type FrmImpostazioniInizialiArgs = {
    
}

export type FrmImpostazioniInizialiTypeRef = React.ElementRef<typeof FrmImpostazioniIniziali>;

export const FrmImpostazioniIniziali = forwardRef((props: any, ref: Ref<FrmImpostazioniInizialiArgs>) => {
    const { doorConfigurationBuilder } = useStoreSelector(store => store);    
    const [erroreQta, setErroreQta] = useState<string | null>(null);
    
    const dispatcher = useStoreDispatch();
    const {
        setQuantita,
        setTipoInstallazioneTraversa,
        setTipoInstallazioneMontanteSx,
        setTipoInstallazioneMontanteDx,
        setVanoConAltroAccesso,
        setPortinaPedonaleIntegrata,
        setStepVisible,
        setIsDebordante,
        setLarghezzaForo,
        setAltezzaForo,
        setLarghezzaPorta,
        setAltezzaPorta,
        setProfonditaGarage,
        setAltezzaArchitrave,
        setSpessoreArchitrave,
        setLarghezzaSpallettaSx,
        setSpessoreSpallettaSx,
        setLarghezzaSpallettaDx,
        setSpessoreSpallettaDx,
    } = useDoorConfigurationBuilderActions();
    
    const formik = useFormik<FrmImpostazioniInizialiData>({
        initialValues: {
            vanoConAltroAccesso: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.vanoConAltroAccesso : false,
            portinaPedonaleIntegrata: doorConfigurationBuilder.configuration?.portinaPedonaleIntegrata ?? false,
            quantita: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.quantita : 0,
            isDebordante: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.isDebordante ?? true : true,
            tipoInstallazioneTraversa: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.tipoInstallazioneTraversa : InstallationType.oltre_luce,
            tipoInstallazioneMontanteSx: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.tipoInstallazioneMontanteSx : InstallationType.oltre_luce,
            tipoInstallazioneMontanteDx: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.tipoInstallazioneMontanteDx : InstallationType.oltre_luce,
            larghezzaForo: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.larghezzaForo : 0,
            altezzaForo: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.altezzaForo : 0,
            larghezzaPorta: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.larghezzaPorta : 0,
            altezzaPorta: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.altezzaPorta : 0,
            profonditaGarage: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.profonditaGarage : 0,
            altezzaArchitrave: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.altezzaArchitrave : 0,
            spessoreArchitrave: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.spessoreArchitrave : 0,
            larghezzaSpallettaSx: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.larghezzaSpallettaSx : 0,
            spessoreSpallettaSx: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.spessoreSpallettaSx : 0,
            larghezzaSpallettaDx: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.larghezzaSpallettaDx : 0,
            spessoreSpallettaDx: doorConfigurationBuilder.configuration ? doorConfigurationBuilder.configuration.spessoreSpallettaDx : 0,
        },
        onSubmit: (model) => {
            
        }
    });

    const check = () => {
        if (!isValidQta(formik.values.quantita)) throw "Quantità non valida";
    }

    const isValidQta = (value: number): boolean => !isNaN(value) && value > 0;

    useImperativeHandle(ref, () => ({
        
    }));

    return (
        <div>
            <TextField
                variant="outlined"
                label="Quantità"
                type="number"
                className="mt-3 w-full"
                value={formik.values.quantita}
                onChange={(e) => formik.setFieldValue("quantita", Number(e.target.value))}
                onBlur={(e) => {
                    if (!isValidQta(Number(e.target.value))) {
                        setErroreQta("Valore non valido");
                        return;
                    }                            
                    setErroreQta(null);
                    dispatcher(setQuantita(Number(e.target.value)));
                }}
                error={erroreQta != null}
                helperText={erroreQta}
            />

            <FormControl fullWidth className="mt-3">
                <InputLabel id="tipoInstallazioneTraversa">Installazione superiore</InputLabel>
                <Select
                    labelId="tipoInstallazioneTraversa"
                    value={formik.values.tipoInstallazioneTraversa}
                    label="Installazione superiore"
                    onChange={(e) => {
                        formik.setFieldValue("tipoInstallazioneTraversa", e.target.value);
                        dispatcher(setTipoInstallazioneTraversa(e.target.value as InstallationType));
                    }}>
                    <MenuItem value={InstallationType.in_luce}>In Luce</MenuItem>
                    <MenuItem value={InstallationType.oltre_luce}>Oltre Luce</MenuItem>
                </Select>
            </FormControl>
            
            <FormControl fullWidth className="mt-3">
                <InputLabel id="tipoInstallazioneMontanteSx">Installazione laterale - Guida sinistra (vista interna)</InputLabel>
                <Select
                    labelId="tipoInstallazioneMontanteSx"
                    value={formik.values.tipoInstallazioneMontanteSx}
                    label="Installazione laterale - Guida sinistra (vista interna)"
                    onChange={(e) => {
                        formik.setFieldValue("tipoInstallazioneMontanteSx", e.target.value);
                        dispatcher(setTipoInstallazioneMontanteSx(e.target.value as InstallationType));
                    }}>
                    <MenuItem value={InstallationType.in_luce}>In Luce</MenuItem>
                    <MenuItem value={InstallationType.oltre_luce}>Oltre Luce</MenuItem>
                </Select>
            </FormControl>
            
            <FormControl fullWidth className="mt-3">
                <InputLabel id="tipoInstallazioneMontanteDx">Installazione laterale - Guida destra (vista interna)</InputLabel>
                <Select
                    labelId="tipoInstallazioneMontanteDx"
                    value={formik.values.tipoInstallazioneMontanteDx}
                    label="Installazione laterale - Guida destra (vista interna)"
                    onChange={(e) => {
                        formik.setFieldValue("tipoInstallazioneMontanteDx", e.target.value);
                        dispatcher(setTipoInstallazioneMontanteDx(e.target.value as InstallationType));
                    }}>
                    <MenuItem value={InstallationType.in_luce}>In Luce</MenuItem>
                    <MenuItem value={InstallationType.oltre_luce}>Oltre Luce</MenuItem>
                </Select>
            </FormControl>
            {(
                <FormControl fullWidth className="mt-3">
                    <InputLabel id="isDebordante">Debordante</InputLabel>
                    <Select
                        labelId="isDebordante"
                        value={formik.values.isDebordante ? "true" : "false"}
                        label="Debordante"
                        onChange={(e) => {
                            const val = e.target.value === "true";
                            formik.setFieldValue("isDebordante", val);
                            dispatcher(setIsDebordante(val));
                        }}>
                        <MenuItem value="true">Sì</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                    </Select>
                </FormControl>
            )}
            <div className="d-xl-none">
                <TextField
                    variant="outlined"
                    label="Altezza Vuoto"
                    type="number"
                    className="mt-3 w-full"
                    value={formik.values.altezzaForo}
                    onChange={(e) => formik.setFieldValue("altezzaForo", Number(e.target.value))}
                    onBlur={(e) => dispatcher(setAltezzaForo(Number(e.target.value)))}
                />

                <TextField
                    variant="outlined"
                    label="Larghezza Vuoto"
                    type="number"
                    className="mt-3 w-full"
                    value={formik.values.larghezzaForo}
                    onChange={(e) => formik.setFieldValue("larghezzaForo", Number(e.target.value))}
                    onBlur={(e) => dispatcher(setLarghezzaForo(Number(e.target.value)))}
                />

                <TextField
                    variant="outlined"
                    label="Profondità Garage"
                    type="number"
                    className="mt-3 w-full"
                    value={formik.values.profonditaGarage}
                    onChange={(e) => formik.setFieldValue("profonditaGarage", Number(e.target.value))}
                    onBlur={(e) => dispatcher(setProfonditaGarage(Number(e.target.value)))}
                />

                <TextField
                    variant="outlined"
                    label="Altezza Architrave"
                    type="number"
                    className="mt-3 w-full"
                    value={formik.values.altezzaArchitrave}
                    onChange={(e) => formik.setFieldValue("altezzaArchitrave", Number(e.target.value))}
                    onBlur={(e) => dispatcher(setAltezzaArchitrave(Number(e.target.value)))}
                />

                <TextField
                    variant="outlined"
                    label="Spessore Architrave"
                    type="number"
                    className="mt-3 w-full"
                    value={formik.values.spessoreArchitrave}
                    onChange={(e) => formik.setFieldValue("spessoreArchitrave", Number(e.target.value))}
                    onBlur={(e) => dispatcher(setSpessoreArchitrave(Number(e.target.value)))}
                />

                <TextField
                    variant="outlined"
                    label="Larghezza Spalletta Destra"
                    type="number"
                    className="mt-3 w-full"
                    value={formik.values.larghezzaSpallettaDx}
                    onChange={(e) => formik.setFieldValue("larghezzaSpallettaDx", Number(e.target.value))}
                    onBlur={(e) => dispatcher(setLarghezzaSpallettaDx(Number(e.target.value)))}
                />

                <TextField
                    variant="outlined"
                    label="Spessore Spalletta Destra"
                    type="number"
                    className="mt-3 w-full"
                    value={formik.values.spessoreSpallettaDx}
                    onChange={(e) => formik.setFieldValue("spessoreSpallettaDx", Number(e.target.value))}
                    onBlur={(e) => dispatcher(setSpessoreSpallettaDx(Number(e.target.value)))}
                />

                <TextField
                    variant="outlined"
                    label="Larghezza Spalletta Sinistra"
                    type="number"
                    className="mt-3 w-full"
                    value={formik.values.larghezzaSpallettaSx}
                    onChange={(e) => formik.setFieldValue("larghezzaSpallettaSx", Number(e.target.value))}
                    onBlur={(e) => dispatcher(setLarghezzaSpallettaSx(Number(e.target.value)))}
                />

                <TextField
                    variant="outlined"
                    label="Spessore Spalletta Sinistra"
                    type="number"
                    className="mt-3 w-full"
                    value={formik.values.spessoreSpallettaSx}
                    onChange={(e) => formik.setFieldValue("spessoreSpallettaSx", Number(e.target.value))}
                    onBlur={(e) => dispatcher(setSpessoreSpallettaSx(Number(e.target.value)))}
                />
            </div>

            <FormControlLabel 
                className="w-full mt-4" 
                control={
                    <Checkbox                            
                        checked={formik.values.vanoConAltroAccesso}
                        onChange={(e) => {
                            formik.setFieldValue("vanoConAltroAccesso", e.target.checked);
                            dispatcher(setVanoConAltroAccesso(e.target.checked));
                        }}
                        sx={{marginTop: "0", marginBottom: "0"}}
                    />
                } 
                label="Locale garage con altro accesso"
                sx={{marginTop: "0", marginBottom: "0", textTransform: "uppercase"}}
            />

            <FormControlLabel
                className="w-full"
                control={
                    <Checkbox
                        checked={formik.values.portinaPedonaleIntegrata}
                        onChange={(e) => {
                            formik.setFieldValue("portinaPedonaleIntegrata", e.target.checked);
                            dispatcher(setPortinaPedonaleIntegrata(e.target.checked));
                            dispatcher(setStepVisible({ stepSlug: "portina-pedonale", isVisible: e.target.checked }));
                            dispatcher(setStepVisible({ stepSlug: "maniglieria", isVisible: !e.target.checked }));
                        }}
                        sx={{marginTop: "0", marginBottom: "0"}}
                    />
                }
                label="Con portina pedonale integrata"
                sx={{marginTop: "0", marginBottom: "0", textTransform: "uppercase"}}
            />

            {/* <FormControlLabel 
                className="w-full"
                control={
                    <Checkbox                            
                        checked={formik.values.portinaPedonaleAffiancata}
                        onChange={(e) => {
                            formik.setFieldValue("portinaPedonaleAffiancata", e.target.checked);
                            dispatcher(setPortinaPedonaleAffiancata(e.target.checked));
                        }}
                        sx={{marginTop: "0", marginBottom: "0"}}
                    />
                } 
                label="Aggiungi portina pedonale affiancata"  
                sx={{marginTop: "0", marginBottom: "0", textTransform: "uppercase"}}
            /> */}

        </div>
    );
});